import { HOUR, MINUTE, toIso, toMs } from '../../domain/time';
import { normalizeIsraeliPhone } from '../../domain/phone';
import type { AuthAdapter, Clock, KeyValueStore, Session, Unsubscribe } from '../types';

export { normalizeIsraeliPhone } from '../../domain/phone';

const SESSION_KEY = 'auth.session';
const OTP_KEY = 'auth.otp';
const OTP_TTL = 5 * MINUTE;
const MAX_ATTEMPTS = 5;
const MAX_REQUESTS = 5;
const SESSION_TTL = 30 * 24 * HOUR;

interface OtpEntry { code: string; expiresAt: number; attempts: number; requests: number; windowStart: number }

/** Mock phone OTP: the code is "sent" nowhere; the demo UI reads it with getDemoCode(phone). */
export function createMockAuth(deps: { kv: KeyValueStore; clock: Clock; random?: () => number }): AuthAdapter {
  const { kv, clock } = deps;
  const random = deps.random ?? Math.random;
  const listeners = new Set<(s: Session | undefined) => void>();

  const readOtps = (): Record<string, OtpEntry> => {
    try { return JSON.parse(kv.get(OTP_KEY) ?? '{}') as Record<string, OtpEntry>; } catch { return {}; }
  };
  const writeOtps = (o: Record<string, OtpEntry>): void => kv.set(OTP_KEY, JSON.stringify(o));
  const readSession = (): Session | undefined => {
    try {
      const s = JSON.parse(kv.get(SESSION_KEY) ?? 'null') as Session | null;
      return s && toMs(s.expiresAt) > clock.now() ? s : undefined;
    } catch { return undefined; }
  };
  const writeSession = (s: Session | undefined): void => {
    if (s) kv.set(SESSION_KEY, JSON.stringify(s)); else kv.remove(SESSION_KEY);
    for (const cb of [...listeners]) cb(s);
  };

  return {
    label: 'Phone verification (demo, no SMS sent)',
    async requestOtp(phone) {
      const p = normalizeIsraeliPhone(phone);
      if (!p.ok) return { ok: false, reason: 'invalid_phone' };
      const all = readOtps();
      const prev = all[p.e164];
      const now = clock.now();
      const inWindow = prev !== undefined && now - prev.windowStart < 10 * MINUTE;
      if (inWindow && prev.requests >= MAX_REQUESTS) return { ok: false, reason: 'rate_limited' };
      const code = String(100000 + Math.floor(random() * 900000));
      all[p.e164] = { code, expiresAt: now + OTP_TTL, attempts: 0, requests: inWindow ? prev.requests + 1 : 1, windowStart: inWindow ? prev.windowStart : now };
      writeOtps(all);
      return { ok: true, phone: p.e164 };
    },
    async verifyOtp(phone, code, opts) {
      const p = normalizeIsraeliPhone(phone);
      if (!p.ok) return { ok: false, reason: 'invalid_phone' };
      const all = readOtps();
      const entry = all[p.e164];
      if (!entry) return { ok: false, reason: 'no_code' };
      if (entry.expiresAt <= clock.now()) return { ok: false, reason: 'expired' };
      if (entry.attempts >= MAX_ATTEMPTS) return { ok: false, reason: 'too_many_attempts' };
      if (entry.code !== code.trim()) {
        all[p.e164] = { ...entry, attempts: entry.attempts + 1 };
        writeOtps(all);
        return { ok: false, reason: 'wrong_code' };
      }
      delete all[p.e164];
      writeOtps(all);
      const now = clock.now();
      const session: Session = {
        token: `s_${Math.floor(random() * 1e12).toString(36)}${now.toString(36)}`, phone: p.e164,
        createdAt: toIso(now), expiresAt: toIso(now + SESSION_TTL),
      };
      if (opts?.persist !== false) writeSession(session);
      return { ok: true, session };
    },
    getSession: readSession,
    linkSession(ownerId, businessId) {
      const s = readSession();
      if (s) writeSession({ ...s, ownerId, businessId });
    },
    signOut: () => writeSession(undefined),
    getDemoCode(phone) {
      const p = normalizeIsraeliPhone(phone);
      const entry = p.ok ? readOtps()[p.e164] : undefined;
      return entry && entry.expiresAt > clock.now() ? entry.code : undefined;
    },
    subscribe(cb): Unsubscribe { listeners.add(cb); return () => void listeners.delete(cb); },
  };
}
